import type { AxiosInstance } from 'axios';
import type { Page } from '@core/types/api.types';

const describe = (v: unknown): string => (v === null ? 'null' : typeof v);

/**
 * Follow a cursor-paginated endpoint to completion and return the flattened
 * items. The UI works with plain arrays; pagination is an API detail we absorb
 * here. A hard page cap guards against a runaway cursor, and each page's shape
 * is validated so a bad response (e.g. an HTML error page in live mode)
 * surfaces as an error state rather than a crash.
 */
export async function fetchAllPages<T>(
  client: AxiosInstance,
  path: string,
  params: Record<string, string | number | boolean | undefined> = {},
  maxPages = 50,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null | undefined;
  for (let i = 0; i < maxPages; i += 1) {
    const res = await client.get<Page<T>>(path, {
      params: { ...params, ...(cursor ? { cursor } : {}) },
    });
    const body: unknown = res.data;
    if (body === null || typeof body !== 'object' || !Array.isArray((body as { items?: unknown }).items)) {
      throw new Error(
        `Expected a paginated { items: [] } response from ${path} but received ${describe(body)}.`,
      );
    }
    const pageData = body as Page<T>;
    items.push(...pageData.items);
    cursor = pageData.next_cursor;
    if (!cursor) break;
  }
  return items;
}
